import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { showSuccess, showError, showInfo, showWarning, showQuickSuccess } from '../utils/notifications';
import { 
  ArrowLeftIcon,
  PhotoIcon,
  QrCodeIcon,
  ArrowTopRightOnSquareIcon
} from '@heroicons/react/24/outline';
import API_CONFIG from '../config';
import BarcodeScanner from './BarcodeScanner';
import { getProductByBarcode, getOpenFoodFactsUrl } from '../services/openFoodFactsService';

const EditProduct = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [showScanner, setShowScanner] = useState(false);
  const [barcode, setBarcode] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    category_id: '',
    description: '',
    image_url: '',
    price: '',
    unit_price: '',
    unit_type: '',
    barcode: ''
  });

  const fetchCategories = useCallback(async () => {
    try {
      const response = await fetch(`${API_CONFIG.BASE_URL}/categories`, {
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Erreur lors de la récupération des catégories: ${response.status}`);
      }

      const categoriesData = await response.json();
      if (!Array.isArray(categoriesData)) {
        showError('Format de données invalide pour les catégories');
        return;
      }
      if (categoriesData.length === 0) {
        showWarning('Aucune catégorie disponible');
      }
      setCategories(categoriesData);
    } catch (error) {
      showError(`Erreur lors du chargement des catégories: ${error.message}`);
    }
  }, []);

  const fetchProductData = useCallback(async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API_CONFIG.BASE_URL}/products/${productId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('token');
          navigate('/login');
          return;
        } else if (response.status === 404) {
          navigate('/products');
          return;
        }
        throw new Error('Erreur lors de la récupération du produit');
      }

      const productData = await response.json();
      
      // Pré-remplir le formulaire avec les données du produit
      setFormData({
        name: productData.name || '',
        category_id: productData.category?.id || '',
        description: productData.description || '',
        image_url: productData.image_url || '',
        price: productData.price?.toString() || '',
        unit_price: productData.unit_price?.toString() || '',
        unit_type: productData.unit_type || '',
        barcode: productData.barcode || ''
      });
      
      setBarcode(productData.barcode || '');
    } catch (error) {
      console.error('Erreur:', error);
      showError('Erreur lors du chargement du produit');
    } finally {
      setLoading(false);
    }
  }, [productId, navigate]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }

    fetchCategories();
    fetchProductData();
  }, [navigate, fetchCategories, fetchProductData]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleBarcodeScan = async (scannedBarcode) => {
    setBarcode(scannedBarcode);
    setShowScanner(false);
    
    showInfo('Récupération des informations du produit...');
    
    try {
      const result = await getProductByBarcode(scannedBarcode);
      
      if (result.success) {
        const product = result.product;
        
        // Mettre à jour le formulaire avec les données d'OpenFoodFacts
        setFormData(prev => ({
          ...prev,
          name: product.name || prev.name,
          description: product.description || prev.description,
          image_url: product.image || prev.image_url,
          barcode: scannedBarcode
        }));
        
        showSuccess('Informations du produit récupérées avec succès !');
      } else {
        showWarning(result.error || 'Produit non trouvé dans OpenFoodFacts');
        // On garde quand même le code-barres pour référence
        setFormData(prev => ({
          ...prev,
          barcode: scannedBarcode
        }));
      }
    } catch (error) {
      console.error('Erreur lors de la récupération:', error);
      showError('Erreur lors de la récupération des informations du produit');
      // On garde quand même le code-barres pour référence
      setFormData(prev => ({
        ...prev,
        barcode: scannedBarcode
      }));
    }
  };

  const handleOpenFoodFactsClick = () => {
    if (barcode) {
      const url = getOpenFoodFactsUrl(barcode);
      window.open(url, '_blank');
    }
  };

  // Fonction pour compresser une image avant l'upload
  const compressImage = (file, maxWidth = 800, quality = 0.6) => {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();
      
      img.onload = () => {
        // Calculer les nouvelles dimensions
        let { width, height } = img;
        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }
        
        // Configurer le canvas
        canvas.width = width;
        canvas.height = height;
        
        // Dessiner l'image compressée
        ctx.drawImage(img, 0, 0, width, height);
        
        // Convertir en blob avec une qualité plus basse
        canvas.toBlob((blob) => {
          // Créer un nouveau fichier avec le blob compressé
          const compressedFile = new File([blob], file.name, {
            type: 'image/jpeg',
            lastModified: Date.now()
          });
          
          resolve(compressedFile);
        }, 'image/jpeg', quality);
      };
      
      img.src = URL.createObjectURL(file);
    });
  };

  const handleTakePhoto = async () => {
    // Vérifier si l'appareil photo est disponible
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      showError('L\'appareil photo n\'est pas disponible sur votre appareil');
      return;
    }

    // Créer un input file caché pour la prise de photo
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.capture = 'environment'; // Utiliser l'appareil photo arrière sur mobile
    
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (file) {
        try {
          setLoading(true);
          showInfo('Compression de l\'image...');
          
          // Vérifier la taille du fichier
          const maxSize = 5 * 1024 * 1024; // 5MB au lieu de 10MB
          if (file.size > maxSize) {
            showError('L\'image est trop volumineuse (max 5MB). Veuillez choisir une image plus petite.');
            return;
          }
          
          // Compresser l'image avec des paramètres plus agressifs
          const compressedFile = await compressImage(file, 800, 0.6);
          
          // Vérifier la taille après compression
          if (compressedFile.size > 2 * 1024 * 1024) { // 2MB max après compression
            showError('L\'image reste trop volumineuse après compression. Veuillez choisir une image plus petite.');
            return;
          }
          
          const token = localStorage.getItem('token');
          
          // Créer un FormData pour l'upload
          const formData = new FormData();
          formData.append('file', compressedFile);
          
          // Upload de l'image
          const response = await fetch(`${API_CONFIG.BASE_URL}/products/upload-image`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`
            },
            body: formData
          });
          
          if (!response.ok) {
            let errorMessage = 'Erreur lors de l\'upload de l\'image';
            
            if (response.status === 413) {
              errorMessage = 'L\'image est encore trop volumineuse. Veuillez choisir une image plus petite.';
            } else {
              // Essayer de parser la réponse JSON
              try {
                const errorData = await response.json();
                errorMessage = errorData.detail || errorMessage;
              } catch (parseError) {
                // Si la réponse n'est pas du JSON, c'est probablement une erreur HTML
                console.error('Réponse non-JSON reçue:', await response.text());
                errorMessage = 'Erreur serveur lors de l\'upload de l\'image';
              }
            }
            
            throw new Error(errorMessage);
          }
          
          const result = await response.json();
          
          // Mettre à jour le formulaire avec l'image compressée
          setFormData(prev => ({
            ...prev,
            image_url: result.image_data,
            image_data: result.image_data
          }));
          
          // Afficher les informations de compression
          const originalSize = (file.size / 1024).toFixed(1);
          const compressedSize = (compressedFile.size / 1024).toFixed(1);
          const compressionRatio = ((1 - compressedFile.size / file.size) * 100).toFixed(1);
          const compressionInfo = `Image compressée: ${compressionRatio}% de réduction (${originalSize}KB → ${compressedSize}KB)`;
          showSuccess(`Photo prise avec succès ! ${compressionInfo}`);
          
        } catch (error) {
          console.error('Erreur upload:', error);
          showError(error.message || 'Erreur lors de l\'upload de l\'image');
        } finally {
          setLoading(false);
        }
      }
    };
    
    input.click();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      showError('Le nom du produit est requis');
      return;
    }

    if (!formData.category_id) {
      showError('Veuillez sélectionner une catégorie');
      return;
    }

    if (!formData.price || parseFloat(formData.price) <= 0) {
      showError('Veuillez saisir un prix valide');
      return;
    }

    setLoading(true);
    const token = localStorage.getItem('token');

    try {
      const productData = {
        name: formData.name.trim(),
        category_id: parseInt(formData.category_id),
        description: formData.description.trim(),
        image_url: formData.image_url,
        price: parseFloat(formData.price),
        unit_price: formData.unit_price ? parseFloat(formData.unit_price) : null,
        unit_type: formData.unit_type || null,
        barcode: formData.barcode || null
      };

      const response = await fetch(`${API_CONFIG.BASE_URL}/products/${productId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(productData)
      });

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('token');
          navigate('/login');
          return;
        }
        const errorData = await response.json();
        const detail = errorData.detail;
        // FastAPI renvoie une liste d'erreurs de validation (pas une chaîne) sur 422
        const message = Array.isArray(detail)
          ? detail.map(d => d.msg || d).join(', ')
          : detail;
        throw new Error(message || 'Erreur lors de la modification du produit');
      }

      showQuickSuccess('Produit modifié !');
      navigate(`/products/${productId}`);
    } catch (error) {
      console.error('Erreur:', error);
      showError(error.message || 'Erreur lors de la modification du produit');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate(`/products/${productId}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement du produit...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200 flex-shrink-0">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center py-4">
            <button
              onClick={handleBack}
              className="mr-4 p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeftIcon className="w-6 h-6" />
            </button>
            <h1 className="text-xl font-semibold text-gray-900">Modifier le produit</h1>
          </div>
        </div>
      </div>

      {/* Contenu principal avec scroll */}
      <div className="flex-1 min-h-0 overflow-y-auto scrollbar-hide">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-6 pb-32">
          {/* Section Scan de code-barres */}
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-4 mb-6 border border-blue-100 shadow-sm">
            <div className="text-center mb-2">
              <div className="inline-flex items-center justify-center w-8 h-8 bg-blue-100 rounded-full mb-1">
                <QrCodeIcon className="w-4 h-4 text-blue-600" />
              </div>
              <h3 className="text-sm font-semibold text-gray-800 mb-0.5">Scanner un code-barres</h3>
              <p className="text-xs text-gray-600">Remplissez automatiquement les informations du produit</p>
            </div>
            
            <button
              type="button"
              onClick={() => setShowScanner(true)}
              className="w-full flex items-center justify-center px-4 py-3 bg-gradient-to-r from-blue-500 via-blue-600 to-indigo-600 hover:from-blue-600 hover:via-blue-700 hover:to-indigo-700 text-white rounded-lg shadow-md hover:shadow-lg transition-all duration-300 transform hover:scale-[1.01] active:scale-[0.99] font-medium text-sm"
            >
              <QrCodeIcon className="w-4 h-4 mr-2" />
              <span>Scanner maintenant</span>
            </button>
            
            {barcode && (
              <div className="mt-4 p-4 bg-white rounded-xl border border-green-200 shadow-sm">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center mr-3">
                      <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-sm text-green-700 font-medium">Code-barres détecté</p>
                      <p className="font-mono text-base font-semibold text-gray-800">{barcode}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      type="button"
                      onClick={handleOpenFoodFactsClick}
                      className="flex items-center px-3 py-1.5 bg-green-500 hover:bg-green-600 text-white rounded-lg text-sm transition-colors"
                    >
                      <ArrowTopRightOnSquareIcon className="w-4 h-4 mr-1" />
                      OpenFoodFacts
                    </button>
                    <button
                      type="button"
                      onClick={() => setBarcode('')}
                      className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-full hover:bg-gray-100"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Nom du produit */}
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                Nom du produit *
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                placeholder="Ex: Pommes Golden"
                required
              />
            </div>

            {/* Catégorie */}
            <div>
              <label htmlFor="category_id" className="block text-sm font-medium text-gray-700 mb-2">
                Catégorie *
              </label>
              <select
                id="category_id"
                name="category_id"
                value={formData.category_id}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                required
              >
                <option value="">Sélectionner une catégorie</option>
                {categories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Prix */}
            <div>
              <label htmlFor="price" className="block text-sm font-medium text-gray-700 mb-2">
                Prix du produit *
              </label>
              <div className="relative">
                <input
                  type="number"
                  id="price"
                  name="price"
                  value={formData.price}
                  onChange={handleInputChange}
                  step="0.01"
                  min="0"
                  className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder="0.00"
                  required
                />
                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">€</span>
              </div>
            </div>

            {/* Prix unitaire */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="unit_price" className="block text-sm font-medium text-gray-700 mb-2">
                  Prix unitaire (optionnel)
                </label>
                <div className="relative">
                  <input
                    type="number"
                    id="unit_price"
                    name="unit_price"
                    value={formData.unit_price}
                    onChange={handleInputChange}
                    step="0.01"
                    min="0"
                    className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="0.00"
                  />
                  <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">€</span>
                </div>
              </div>
              <div>
                <label htmlFor="unit_type" className="block text-sm font-medium text-gray-700 mb-2">
                  Unité
                </label>
                <select
                  id="unit_type"
                  name="unit_type"
                  value={formData.unit_type}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                >
                  <option value="">Sélectionner</option>
                  <option value="kg">kg</option>
                  <option value="g">g</option>
                  <option value="L">L</option>
                  <option value="ml">ml</option>
                  <option value="unit">unité</option>
                  <option value="pack">pack</option>
                  <option value="bouteille">bouteille</option>
                  <option value="boîte">boîte</option>
                </select>
              </div>
            </div>

            {/* Description */}
            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
                Description (optionnel)
              </label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                placeholder="Description du produit..."
              />
            </div>

            {/* Code-barres */}
            <div>
              <label htmlFor="barcode" className="block text-sm font-medium text-gray-700 mb-2">
                Code-barres (optionnel)
              </label>
              <input
                type="text"
                id="barcode"
                name="barcode"
                value={formData.barcode}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                placeholder="Code-barres du produit"
              />
            </div>

            {/* Image Upload */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Photo du produit
              </label>
              <div className="space-y-3">
                {/* Aperçu de l'image */}
                {formData.image_url && (
                  <div className="relative">
                    <img
                      src={formData.image_url}
                      alt="Aperçu"
                      className="w-full h-48 object-cover rounded-lg border border-gray-200"
                    />
                    <button
                      type="button"
                      onClick={() => setFormData({...formData, image_url: ''})}
                      className="absolute top-2 right-2 bg-red-500 hover:bg-red-600 text-white rounded-full p-1 transition-colors"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                )}
                
                {/* Bouton de prise de photo */}
                <button
                  type="button"
                  onClick={handleTakePhoto}
                  className="w-full flex items-center justify-center px-4 py-3 bg-green-500 hover:bg-green-600 text-white rounded-lg transition-colors"
                >
                  <PhotoIcon className="w-5 h-5 mr-2" />
                  Prendre une photo
                </button>
                
                <p className="text-sm text-gray-500">
                  {formData.image_url ? 'Photo prise avec succès' : 'Prenez une photo du produit ou scannez un code-barres'}
                </p>
              </div>
            </div>

            {/* Boutons */}
            <div className="flex space-x-4 pt-6">
              <button
                type="button"
                onClick={handleBack}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex-1 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    Modification en cours...
                  </div>
                ) : (
                  'Modifier le produit'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Scanner de code-barres */}
      {showScanner && (
        <BarcodeScanner
          onScan={handleBarcodeScan}
          onClose={() => setShowScanner(false)}
        />
      )}
    </div>
  );
};

export default EditProduct; 